import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DropService from '../services/DropService';
import { 
  FiMapPin, 
  FiPlus, 
  FiGift, 
  FiDollarSign, 
  FiTarget,
  FiExternalLink,
  FiRefreshCw,
  FiShield,
  FiEdit,
  FiTrash2,
  FiAward,
  FiPhone,
  FiMail
} from 'react-icons/fi';

const Drops = () => {
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL'); // ALL, ACTIVE, CLAIMED, EXPIRED
  const [deletingId, setDeletingId] = useState(null);
  const [selectedDropWinnerModal, setSelectedDropWinnerModal] = useState(null);
  const [winnerAnalytics, setWinnerAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  useEffect(() => {
    fetchAllDrops();
  }, []);

  const fetchAllDrops = async () => {
    setLoading(true);
    try {
      const res = await DropService.getAllDrops();
      const list = Array.isArray(res) ? res : (res.drops || res.data || []);
      setDrops(list);
    } catch (err) {
      console.error('Error fetching admin drops:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenWinnerModal = async (drop) => {
    setSelectedDropWinnerModal(drop);
    setLoadingAnalytics(true);
    setWinnerAnalytics(null);
    try {
      const res = await DropService.getDropAnalytics(drop._id);
      if (res && (res.analytics || res.data)) {
        setWinnerAnalytics(res.analytics || res.data);
      }
    } catch (err) {
      console.error('Error fetching drop analytics:', err);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const handleDeleteDrop = async (dropId, title) => {
    if (!window.confirm(`Are you sure you want to delete drop "${title || 'Drop'}"? This action cannot be undone.`)) {
      return;
    }

    setDeletingId(dropId);
    try {
      await DropService.deleteDrop(dropId);
      setDrops(prev => prev.filter(d => d._id !== dropId));
    } catch (err) {
      console.error('Error deleting drop:', err);
      alert(err.response?.data?.message || err.message || 'Failed to delete drop.');
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusBadge = (status, winnersCount, totalWinners) => {
    const isFullyClaimed = winnersCount >= totalWinners && totalWinners > 0;
    if (status === 'CLAIMED' || isFullyClaimed) {
      return (
        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200 uppercase">
          🎉 Claimed ({winnersCount || 0}/{totalWinners || 1})
        </span>
      );
    }
    if (status === 'EXPIRED') {
      return (
        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200 uppercase">
          Expired
        </span>
      );
    }
    if (status === 'HUNTING') {
      return (
        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200 uppercase animate-pulse">
          ⚡ Hunter Nearby
        </span>
      );
    }
    return (
      <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-purple-100 text-purple-800 border border-purple-200 uppercase">
        🟢 Live & Active
      </span>
    );
  };

  const filteredDrops = drops.filter(d => {
    if (filter === 'ACTIVE') return d.status === 'ACTIVE' || d.status === 'HUNTING';
    if (filter === 'CLAIMED') return d.status === 'CLAIMED' || (d.winnersCount > 0);
    if (filter === 'EXPIRED') return d.status === 'EXPIRED';
    return true;
  });

  const totalPrizePoolSum = drops.reduce((sum, d) => sum + (d.rewardAmount || d.rewardConfig?.totalPrizePool || 0), 0);
  const activeDropsCount = drops.filter(d => d.status === 'ACTIVE' || d.status === 'HUNTING').length;
  const claimedCount = drops.filter(d => d.status === 'CLAIMED' || (d.winnersCount > 0)).length;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
            <span>Platform GPS Cash & Rewards Drops</span>
            <span className="text-xs bg-purple-100 text-purple-800 font-bold px-2.5 py-0.5 rounded-full border border-purple-200 uppercase">
              Admin Overview
            </span>
          </h1>
          <p className="text-xs text-slate-500 font-medium mt-0.5">
            Monitor and manage all GPS Cash, Movie Ticket, and Gift Card Drops across the entire Tapcoe platform!
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchAllDrops}
            className="p-2.5 rounded-xl bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 transition-all shadow-sm"
            title="Refresh Drops"
          >
            <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          <Link
            to="/app/drops/create"
            className="py-3 px-5 rounded-xl font-black text-xs text-white bg-purple-700 hover:bg-purple-800 transition-all shadow-md shadow-purple-200 flex items-center gap-2 shrink-0"
          >
            <FiPlus className="w-4 h-4" />
            <span>+ Host Admin Drop</span>
          </Link>
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="p-5 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-xl">
            <FiTarget />
          </div>
          <div>
            <span className="text-[10px] text-slate-500 uppercase font-bold block">Total Platform Drops</span>
            <span className="text-xl font-black text-slate-900">{drops.length}</span>
          </div>
        </div>

        <div className="p-5 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xl">
            <FiDollarSign />
          </div>
          <div>
            <span className="text-[10px] text-slate-500 uppercase font-bold block">Total Prize Value</span>
            <span className="text-xl font-black text-slate-900">₹{totalPrizePoolSum.toLocaleString('en-IN')}</span>
          </div>
        </div>

        <div className="p-5 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-xl">
            <FiGift />
          </div>
          <div>
            <span className="text-[10px] text-slate-500 uppercase font-bold block">Live Active Drops</span>
            <span className="text-xl font-black text-slate-900">{activeDropsCount}</span>
          </div>
        </div>

        <div className="p-5 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-xl">
            🎉
          </div>
          <div>
            <span className="text-[10px] text-slate-500 uppercase font-bold block">Claimed Drops</span>
            <span className="text-xl font-black text-slate-900">{claimedCount}</span>
          </div>
        </div>
      </div>

      {/* Filter Tabs & Table Container */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Platform Drops Directory</h2>

          {/* Filter Pills */}
          <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-xl text-xs font-bold">
            {['ALL', 'ACTIVE', 'CLAIMED', 'EXPIRED'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-lg transition-all ${
                  filter === f ? 'bg-white text-purple-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-400 text-xs font-semibold">
            Loading platform drops...
          </div>
        ) : filteredDrops.length === 0 ? (
          <div className="p-12 text-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-purple-50 text-purple-700 mx-auto flex items-center justify-center text-2xl font-black">
              🎯
            </div>
            <h3 className="text-sm font-bold text-slate-900">No Drops Found</h3>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              No drops matching the selected filter. Create a new Admin GPS Drop to place rewards on the map!
            </p>
            <Link
              to="/app/drops/create"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs text-white bg-purple-700 hover:bg-purple-800 transition-all shadow-sm"
            >
              <FiPlus className="w-4 h-4" />
              <span>+ Host Admin Drop</span>
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-700">
              <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-bold text-slate-500">
                <tr>
                  <th className="py-3.5 px-5">Drop Title & Creator</th>
                  <th className="py-3.5 px-4">Reward Type & Value</th>
                  <th className="py-3.5 px-4">Location</th>
                  <th className="py-3.5 px-4">Status</th>
                  <th className="py-3.5 px-4">Expires At</th>
                  <th className="py-3.5 px-5 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredDrops.map((drop) => {
                  const prize = drop.rewardAmount || drop.rewardConfig?.totalPrizePool || 0;
                  const winnersCount = (drop.winners?.length) || drop.winnersCount || 0;
                  const totalWinners = drop.rewardConfig?.totalWinners || 1;
                  const creatorObj = drop.creatorId;
                  const creatorName = typeof creatorObj === 'object'
                    ? (creatorObj?.name || creatorObj?.brandName || drop.creatorName || 'Tapcoe Host')
                    : (drop.creatorName || 'Tapcoe Host');
                  const hasWinners = winnersCount > 0 || drop.status === 'CLAIMED';

                  return (
                    <tr key={drop._id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-4 px-5">
                        <div className="font-bold text-slate-900 text-sm flex items-center gap-2 flex-wrap">
                          <span>{drop.title}</span>
                          <span className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase shrink-0 border ${
                            drop.type === 'PHYSICAL'
                              ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                              : 'bg-purple-100 text-purple-800 border-purple-300'
                          }`}>
                            {drop.type === 'PHYSICAL' ? '🟢 PHYSICAL' : '🟣 DIGITAL'}
                          </span>
                          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 uppercase shrink-0">
                            ❤️ {drop.lives || 3} Lives
                          </span>
                          {drop.type === 'PHYSICAL' && drop.isOtpRequired && (
                            <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300 uppercase shrink-0 flex items-center gap-1">
                              🔑 OTP: <span className="font-mono">{drop.otpCode || 'Required'}</span>
                            </span>
                          )}
                          {drop.type === 'PHYSICAL' && drop.hunterLimit > 0 && (
                            <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-900 border border-blue-300 uppercase shrink-0">
                              👥 Limit: {drop.hunterCount || 0}/{drop.hunterLimit}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 text-slate-500 text-[11px] mt-1">
                          <span className="font-semibold text-purple-800 flex items-center gap-1">
                            <FiShield className="w-3 h-3 text-purple-600" />
                            {creatorName}
                          </span>
                          <span>•</span>
                          <span>{drop.rewardType || 'CASH'}</span>
                        </div>
                      </td>

                      <td className="py-4 px-4">
                        <span className="font-black text-slate-900 text-sm block">
                          {(drop.rewardType && drop.rewardType !== 'CASH' && drop.rewardValue?.includes('Cash Prize Pool'))
                            ? (drop.rewardType === 'TICKET' ? `₹${prize} Movie / Event Pass` : drop.rewardType === 'GIFT_CARD' ? `₹${prize} Gift Card` : `₹${prize} Sponsored Gift`)
                            : (drop.rewardValue || `₹${prize.toLocaleString('en-IN')}`)}
                        </span>
                        <span className="block text-[10px] text-slate-500 font-semibold mt-0.5">
                          {totalWinners} Winner{totalWinners > 1 ? 's' : ''} • {drop.rewardType || 'CASH'}
                        </span>
                      </td>

                      <td className="py-4 px-4 text-slate-700">
                        <div className="flex items-center gap-1 text-slate-900 font-medium">
                          <FiMapPin className="text-purple-600 shrink-0" />
                          <span>{drop.locationName || 'GPS Search Zone'}</span>
                        </div>
                        <span className="block text-[10px] font-mono text-slate-400 mt-0.5">
                          {drop.latitude ? drop.latitude.toFixed(4) : '--'}, {drop.longitude ? drop.longitude.toFixed(4) : '--'}
                        </span>
                      </td>

                      <td className="py-4 px-4">
                        {getStatusBadge(drop.status, winnersCount, totalWinners)}
                      </td>

                      <td className="py-4 px-4 text-slate-600 text-[11px]">
                        {drop.expiresAt ? new Date(drop.expiresAt).toLocaleString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        }) : '--'}
                      </td>

                      <td className="py-4 px-5 text-right">
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                          {hasWinners && (
                            <button
                              onClick={() => handleOpenWinnerModal(drop)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[11px] transition-all shadow-xs"
                              title="View Winner Details & Stats"
                            >
                              <FiAward className="w-3.5 h-3.5 text-amber-300" />
                              <span>Winner Info</span>
                            </button>
                          )}

                          <Link
                            to={`/app/drops/edit/${drop._id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-50 hover:bg-purple-100 text-purple-700 font-bold text-[11px] transition-colors border border-purple-200"
                            title="Edit Drop"
                          >
                            <FiEdit className="w-3 h-3" />
                            <span>Edit</span>
                          </Link>

                          <button
                            onClick={() => handleDeleteDrop(drop._id, drop.title)}
                            disabled={deletingId === drop._id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 font-bold text-[11px] transition-colors border border-red-200"
                            title="Delete Drop"
                          >
                            <FiTrash2 className={`w-3 h-3 ${deletingId === drop._id ? 'animate-spin' : ''}`} />
                            <span>Delete</span>
                          </button>

                          <a
                            href={`https://maps.google.com/?q=${drop.latitude},${drop.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-[11px] transition-colors"
                            title="View on Google Maps"
                          >
                            <span>Map</span>
                            <FiExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Winner Details & Hunt Analytics Modal */}
      {selectedDropWinnerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-2xl w-full border border-slate-200 shadow-2xl overflow-hidden animate-fadeIn my-8">
            {/* Modal Header */}
            <div className="p-6 bg-gradient-to-r from-purple-900 via-indigo-900 to-slate-900 text-white flex items-center justify-between">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-purple-300 block">
                  🏆 Claimed Drop Winner & Hunt Stats
                </span>
                <h3 className="text-xl font-black text-white mt-0.5">
                  {selectedDropWinnerModal.title}
                </h3>
              </div>
              <button
                onClick={() => { setSelectedDropWinnerModal(null); setWinnerAnalytics(null); }}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center font-bold text-sm transition-all"
              >
                ✕
              </button>
            </div>

            {/* Modal Content Body */}
            <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
              {loadingAnalytics ? (
                <div className="py-12 text-center text-slate-500 font-bold text-xs flex items-center justify-center gap-2">
                  <FiRefreshCw className="w-5 h-5 animate-spin text-purple-600" />
                  <span>Fetching Winner Contact Info & Hunt Analytics...</span>
                </div>
              ) : (
                <>
                  {/* Winners List */}
                  {(() => {
                    const winners = winnerAnalytics?.winners || selectedDropWinnerModal.winners || [];
                    if (winners.length === 0 && !winnerAnalytics?.winner) {
                      return (
                        <div className="p-6 text-center bg-slate-50 rounded-2xl border border-slate-200">
                          <p className="text-sm font-bold text-slate-700">No winner record registered yet for this drop.</p>
                          <p className="text-xs text-slate-500 mt-1">If this drop was claimed, the winner details will appear here once registered.</p>
                        </div>
                      );
                    }

                    const displayWinners = winners.length > 0 ? winners : [winnerAnalytics.winner];

                    return (
                      <div className="space-y-4">
                        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                          🎉 Claimed Winner(s) Information ({displayWinners.length})
                        </h4>

                        {displayWinners.map((w, idx) => {
                          const user = w.userId || {};
                          const winnerName = w.userName || user.name || user.username || 'Hunter';
                          const mobileNumber = user.mobileNumber || user.phone || w.mobileNumber || 'Not Provided';
                          const email = user.email || w.email || 'Not Provided';
                          const profilePic = user.profilePic || w.userProfilePic || '';
                          const rank = w.rank || (idx + 1);
                          const rewardVal = w.rewardValue || `₹${w.rewardAmount || selectedDropWinnerModal.rewardAmount || 0}`;
                          const claimedDate = w.claimedAt ? new Date(w.claimedAt).toLocaleString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          }) : '--';

                          const distanceMeters = w.verificationData?.distanceMeters ?? w.distanceMeters ?? '--';
                          const accuracyMeters = w.verificationData?.accuracyMeters ?? '--';
                          const deviceId = w.deviceId || 'N/A';

                          const stats = w.huntStats || {};
                          const totalCoveredDist = stats.formattedDistance || (w.verificationData?.distanceMeters ? `${w.verificationData.distanceMeters} m` : '0 m');
                          const huntDuration = stats.formattedDuration || '0m';
                          const routePoints = stats.routePointsCount || 0;
                          const livesUsed = stats.livesUsed || 1;
                          const startLat = stats.startingLatitude;
                          const startLng = stats.startingLongitude;
                          const startTimeStr = stats.startedAt ? new Date(stats.startedAt).toLocaleString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          }) : null;
                          const claimTimeStr = stats.completedAt ? new Date(stats.completedAt).toLocaleString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          }) : null;

                          return (
                            <div key={idx} className="p-5 bg-gradient-to-br from-purple-50/70 via-indigo-50/40 to-white rounded-2xl border border-purple-200 shadow-sm space-y-4">
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                  {profilePic ? (
                                    <img src={profilePic} alt={winnerName} className="w-12 h-12 rounded-full object-cover border-2 border-purple-500 shadow-sm" />
                                  ) : (
                                    <div className="w-12 h-12 rounded-full bg-purple-600 text-white font-black text-lg flex items-center justify-center shadow-sm">
                                      {winnerName.charAt(0).toUpperCase()}
                                    </div>
                                  )}
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-base font-black text-slate-900">{winnerName}</span>
                                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black bg-amber-400 text-slate-950 uppercase shadow-xs">
                                        👑 Rank #{rank}
                                      </span>
                                    </div>
                                    <span className="text-xs text-purple-800 font-bold block mt-0.5">
                                      Reward Won: {rewardVal}
                                    </span>
                                  </div>
                                </div>
                                
                                <div className="text-right">
                                  <span className="text-[10px] text-slate-400 font-bold uppercase block">Claim Timestamp</span>
                                  <span className="text-xs font-bold text-slate-700 block">{claimedDate}</span>
                                </div>
                              </div>

                              {/* Contact Information Grid */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-purple-100/80">
                                <div className="p-3 bg-white rounded-xl border border-slate-200 shadow-xs flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-black text-sm">
                                    <FiPhone />
                                  </div>
                                  <div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Mobile / Phone Number</span>
                                    <span className="text-xs font-black text-slate-900 select-all font-mono">{mobileNumber}</span>
                                  </div>
                                </div>

                                <div className="p-3 bg-white rounded-xl border border-slate-200 shadow-xs flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center font-black text-sm">
                                    <FiMail />
                                  </div>
                                  <div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Email Address</span>
                                    <span className="text-xs font-bold text-slate-900 select-all font-mono">{email}</span>
                                  </div>
                                </div>
                              </div>

                              {/* Full Winner Hunt Journey Analytics Section */}
                              <div className="p-3.5 bg-slate-900 text-white rounded-xl space-y-2.5 shadow-sm">
                                <div className="flex items-center justify-between text-xs font-bold text-purple-300 border-b border-slate-800 pb-2">
                                  <span className="flex items-center gap-1.5">
                                    🏃 Winner Hunt Journey & Path Analytics
                                  </span>
                                  {startLat && startLng && (
                                    <a
                                      href={`https://maps.google.com/?q=${startLat},${startLng}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[10px] text-emerald-400 hover:underline font-bold flex items-center gap-1"
                                      title="Open starting GPS point on Google Maps"
                                    >
                                      <span>Start Location Map</span>
                                      <FiExternalLink className="w-3 h-3" />
                                    </a>
                                  )}
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                                  <div className="p-2 bg-slate-800/80 rounded-lg border border-slate-700/50">
                                    <span className="text-[9px] text-slate-400 font-bold uppercase block">Total Distance</span>
                                    <span className="font-black text-emerald-400 text-xs block mt-0.5">{totalCoveredDist}</span>
                                  </div>

                                  <div className="p-2 bg-slate-800/80 rounded-lg border border-slate-700/50">
                                    <span className="text-[9px] text-slate-400 font-bold uppercase block">Time Taken</span>
                                    <span className="font-black text-amber-300 text-xs block mt-0.5">{huntDuration}</span>
                                  </div>

                                  <div className="p-2 bg-slate-800/80 rounded-lg border border-slate-700/50">
                                    <span className="text-[9px] text-slate-400 font-bold uppercase block">Lives / Taps Used</span>
                                    <span className="font-black text-purple-300 text-xs block mt-0.5">{livesUsed}</span>
                                  </div>

                                  <div className="p-2 bg-slate-800/80 rounded-lg border border-slate-700/50">
                                    <span className="text-[9px] text-slate-400 font-bold uppercase block">GPS Path Points</span>
                                    <span className="font-black text-blue-300 text-xs block mt-0.5">{routePoints} points</span>
                                  </div>
                                </div>

                                {startTimeStr && (
                                  <div className="text-[10px] text-slate-400 flex flex-col sm:flex-row sm:items-center justify-between gap-1 pt-1 font-mono border-t border-slate-800/60">
                                    <span>🚀 Hunt Started: <strong className="text-slate-200">{startTimeStr}</strong></span>
                                    <span>🏁 Claimed At: <strong className="text-slate-200">{claimTimeStr || claimedDate}</strong></span>
                                  </div>
                                )}
                              </div>

                              {/* Verification & Tech Stats */}
                              <div className="grid grid-cols-3 gap-2 text-[11px] text-slate-600 bg-white/60 p-3 rounded-xl border border-purple-100">
                                <div>
                                  <span className="text-[9px] text-slate-400 uppercase font-bold block">Claim Distance</span>
                                  <span className="font-extrabold text-slate-900">{typeof distanceMeters === 'number' ? `${distanceMeters} m` : distanceMeters}</span>
                                </div>
                                <div>
                                  <span className="text-[9px] text-slate-400 uppercase font-bold block">GPS Accuracy</span>
                                  <span className="font-extrabold text-slate-900">{typeof accuracyMeters === 'number' ? `${accuracyMeters} m` : accuracyMeters}</span>
                                </div>
                                <div>
                                  <span className="text-[9px] text-slate-400 uppercase font-bold block">Device Identifier</span>
                                  <span className="font-mono font-bold text-slate-800 truncate block" title={deviceId}>{deviceId ? deviceId.substring(0, 12) + '...' : 'N/A'}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Overall Hunt Statistics */}
                  {winnerAnalytics && (
                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
                      <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                        📊 Hunt Performance Analytics
                      </h4>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="p-3 bg-white rounded-xl border border-slate-200 text-center">
                          <span className="text-[9px] font-bold text-slate-400 uppercase block">Total Hunters</span>
                          <span className="text-base font-black text-slate-900">{winnerAnalytics.totalHunters || 0}</span>
                        </div>
                        <div className="p-3 bg-white rounded-xl border border-slate-200 text-center">
                          <span className="text-[9px] font-bold text-slate-400 uppercase block">Avg Distance</span>
                          <span className="text-base font-black text-slate-900">{winnerAnalytics.formattedAvgDistance || '0 m'}</span>
                        </div>
                        <div className="p-3 bg-white rounded-xl border border-slate-200 text-center">
                          <span className="text-[9px] font-bold text-slate-400 uppercase block">Avg Time Taken</span>
                          <span className="text-base font-black text-slate-900">{winnerAnalytics.formattedAvgDuration || '0m'}</span>
                        </div>
                        <div className="p-3 bg-white rounded-xl border border-slate-200 text-center">
                          <span className="text-[9px] font-bold text-slate-400 uppercase block">Completion Rate</span>
                          <span className="text-base font-black text-emerald-700">{winnerAnalytics.completionRate || '0%'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 text-right">
              <button
                onClick={() => { setSelectedDropWinnerModal(null); setWinnerAnalytics(null); }}
                className="px-5 py-2 rounded-xl bg-slate-800 text-white font-bold text-xs hover:bg-slate-900 transition-all shadow-sm"
              >
                Close Overview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Drops;
